import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import connectionsHelper from "../../../../API/Connections/connectionsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import ConnectionsSettingsAI from "./ConnectionsSettingsAI";

const HASH_ROUTED_SECTIONS = ["profile", "credentials"];

const buildInitialValues = () => ({
  name: "",
  description: "",
  type: "sql",
  host: "",
  port: "",
  username: "",
  passwordSecret: "",
  database: "",
});

const fromConnectionRecord = (connection) => ({ ...connection });

const ConnectionBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const connectionId = params?.connectionId;
  const isEditMode = !!connectionId && connectionId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isConnectionsLoading, setIsConnectionsLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedConnection = useMemo(
    () => (state?.dbConnections || []).find((u) => u?.id === connectionId),
    [state?.dbConnections, connectionId]
  );

  const loadConnections = useCallback(
    async (workspaceID) => {
      try {
        setIsConnectionsLoading(true);
        const connectionsData = await connectionsHelper.getDatabaseConnections(
          workspaceID,
          null
        );
        let connections = connectionsData?.data?.connectionsByWorkspace?.items;
        connections =
          connections
            ?.map((u) => {
              if (u?._deleted !== true)
                return {
                  ...u,
                };
            })
            ?.filter((u) => u != null) || [];
        dispatch("SET_DB_CONNECTIONS", connections || []);
      } catch (error) {
        console.log(error);
        dispatch("SET_DB_CONNECTIONS", []);
      } finally {
        setIsConnectionsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const connections = state?.dbConnections || [];
    const belongsToWorkspace = connections.some((u) => u?.workspaceID === wsId);
    if (connections.length === 0 || !belongsToWorkspace) {
      loadConnections(wsId);
    } else {
      setIsConnectionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!connectionId) {
      history.replace(`/workspaces/${workspaceId}/settings/db_connections`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (connectionId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues(buildInitialValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isConnectionsLoading) return;
    if (editInitRef.current === connectionId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const connection = (state?.dbConnections || []).find(
      (u) => u?.id === connectionId
    );
    if (!connection) {
      const listIsStale = state?.dbConnections?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(
        `/workspaces/${activeWorkspaceId}/settings/db_connections`
      );
      return;
    }
    editInitRef.current = connectionId;
    setInitialMapValues(fromConnectionRecord(connection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connectionId,
    isEditMode,
    isConnectionsLoading,
    state?.dbConnections,
    getActiveWorkspace()?.id,
  ]);

  const sectionGroups = useMemo(() => {
    return [
      {
        id: "setup",
        label: t("connections.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("connections.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "credentials",
            title: t("connections.builderSectionCredentials", {
              defaultValue: "Credentials",
            }),
            icon: AppAssets.icons.credentials,
          },
        ],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/connections/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("connections.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isConnectionsLoading &&
    (!isEditMode || initialMapValues?.id === connectionId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleConnectionCreated = (status, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("conectionCreationSuccessCaption"), {
        type: "success",
      });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEditMode && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/connections/${savedId}`);
      }
    } else {
      AppToasty.show(t("connectionCreationFailedCaption"), {
        type: "danger",
      });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingConnections")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <ConnectionsSettingsAI
            key={connectionId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? connectionId : undefined}
            opt={isEditMode ? selectedConnection : undefined}
            settingType="connection"
            onDbConnectionCreated={handleConnectionCreated}
            onConnectionDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/db_connections`)
            }
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/db_connections`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("connections.builderBackLabel", { defaultValue: "Connections" })}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || selectedConnection?.name || ""
            : t("connections.builderNewConnection", {
                defaultValue: "New connection",
              })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("connections.unsavedChangesSubCaption", {
          defaultValue:
            "This connection has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default ConnectionBuilderScreenContainer;
